/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { SystemTopicEventSubscriptions } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { EventGridManagementClient } from "../eventGridManagementClient.js";
import {
  SimplePollerLike,
  OperationState,
  createHttpPoller,
} from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import {
  EventSubscription,
  SystemTopicEventSubscriptionsListBySystemTopicNextOptionalParams,
  SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  SystemTopicEventSubscriptionsListBySystemTopicResponse,
  SystemTopicEventSubscriptionsGetOptionalParams,
  SystemTopicEventSubscriptionsGetResponse,
  SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  SystemTopicEventSubscriptionsCreateOrUpdateResponse,
  SystemTopicEventSubscriptionsDeleteOptionalParams,
  EventSubscriptionUpdateParameters,
  SystemTopicEventSubscriptionsUpdateOptionalParams,
  SystemTopicEventSubscriptionsUpdateResponse,
  SystemTopicEventSubscriptionsGetFullUrlOptionalParams,
  SystemTopicEventSubscriptionsGetFullUrlResponse,
  SystemTopicEventSubscriptionsGetDeliveryAttributesOptionalParams,
  SystemTopicEventSubscriptionsGetDeliveryAttributesResponse,
  SystemTopicEventSubscriptionsListBySystemTopicNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing SystemTopicEventSubscriptions operations. */
export class SystemTopicEventSubscriptionsImpl
  implements SystemTopicEventSubscriptions
{
  private readonly client: EventGridManagementClient;

  /**
   * Initialize a new instance of the class SystemTopicEventSubscriptions class.
   * @param client Reference to the service client
   */
  constructor(client: EventGridManagementClient) {
    this.client = client;
  }

  /**
   * List event subscriptions that belong to a specific system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param options The options parameters.
   */
  public listBySystemTopic(
    resourceGroupName: string,
    systemTopicName: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  ): PagedAsyncIterableIterator<EventSubscription> {
    const iter = this.listBySystemTopicPagingAll(
      resourceGroupName,
      systemTopicName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySystemTopicPagingPage(
          resourceGroupName,
          systemTopicName,
          options,
          settings,
        );
      },
    };
  }

  private async *listBySystemTopicPagingPage(
    resourceGroupName: string,
    systemTopicName: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EventSubscription[]> {
    let result: SystemTopicEventSubscriptionsListBySystemTopicResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySystemTopic(
        resourceGroupName,
        systemTopicName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySystemTopicNext(
        resourceGroupName,
        systemTopicName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySystemTopicPagingAll(
    resourceGroupName: string,
    systemTopicName: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  ): AsyncIterableIterator<EventSubscription> {
    for await (const page of this.listBySystemTopicPagingPage(
      resourceGroupName,
      systemTopicName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Get an event subscription.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be found.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, systemTopicName, eventSubscriptionName, options },
      getOperationSpec,
    );
  }

  /**
   * Asynchronously creates or updates an event subscription with the specified parameters. Existing
   * event subscriptions will be updated with this API.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be created. Event subscription names
   *                              must be between 3 and 64 characters in length and use alphanumeric letters only.
   * @param eventSubscriptionInfo Event subscription properties containing the destination and filter
   *                              information.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionInfo: EventSubscription,
    options?: SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SystemTopicEventSubscriptionsCreateOrUpdateResponse>,
      SystemTopicEventSubscriptionsCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<SystemTopicEventSubscriptionsCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        systemTopicName,
        eventSubscriptionName,
        eventSubscriptionInfo,
        options,
      },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      SystemTopicEventSubscriptionsCreateOrUpdateResponse,
      OperationState<SystemTopicEventSubscriptionsCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Asynchronously creates or updates an event subscription with the specified parameters. Existing
   * event subscriptions will be updated with this API.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be created. Event subscription names
   *                              must be between 3 and 64 characters in length and use alphanumeric letters only.
   * @param eventSubscriptionInfo Event subscription properties containing the destination and filter
   *                              information.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionInfo: EventSubscription,
    options?: SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      systemTopicName,
      eventSubscriptionName,
      eventSubscriptionInfo,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Delete an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be deleted.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        systemTopicName,
        eventSubscriptionName,
        options,
      },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Delete an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be deleted.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsDeleteOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDelete(
      resourceGroupName,
      systemTopicName,
      eventSubscriptionName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Update an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be updated.
   * @param eventSubscriptionUpdateParameters Updated event subscription information.
   * @param options The options parameters.
   */
  async beginUpdate(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionUpdateParameters: EventSubscriptionUpdateParameters,
    options?: SystemTopicEventSubscriptionsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SystemTopicEventSubscriptionsUpdateResponse>,
      SystemTopicEventSubscriptionsUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<SystemTopicEventSubscriptionsUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        systemTopicName,
        eventSubscriptionName,
        eventSubscriptionUpdateParameters,
        options,
      },
      spec: updateOperationSpec,
    });
    const poller = await createHttpPoller<
      SystemTopicEventSubscriptionsUpdateResponse,
      OperationState<SystemTopicEventSubscriptionsUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
    });
    await poller.poll();
    return poller;
  }

  /**
   * Update an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be updated.
   * @param eventSubscriptionUpdateParameters Updated event subscription information.
   * @param options The options parameters.
   */
  async beginUpdateAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionUpdateParameters: EventSubscriptionUpdateParameters,
    options?: SystemTopicEventSubscriptionsUpdateOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsUpdateResponse> {
    const poller = await this.beginUpdate(
      resourceGroupName,
      systemTopicName,
      eventSubscriptionName,
      eventSubscriptionUpdateParameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Get the full endpoint URL for an event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription.
   * @param options The options parameters.
   */
  getFullUrl(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetFullUrlOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetFullUrlResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, systemTopicName, eventSubscriptionName, options },
      getFullUrlOperationSpec,
    );
  }

  /**
   * List event subscriptions that belong to a specific system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param options The options parameters.
   */
  private _listBySystemTopic(
    resourceGroupName: string,
    systemTopicName: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsListBySystemTopicResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, systemTopicName, options },
      listBySystemTopicOperationSpec,
    );
  }

  /**
   * Get all delivery attributes for an event subscription.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription.
   * @param options The options parameters.
   */
  getDeliveryAttributes(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetDeliveryAttributesOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetDeliveryAttributesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, systemTopicName, eventSubscriptionName, options },
      getDeliveryAttributesOperationSpec,
    );
  }

  /**
   * ListBySystemTopicNext
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param nextLink The nextLink from the previous successful call to the ListBySystemTopic method.
   * @param options The options parameters.
   */
  private _listBySystemTopicNext(
    resourceGroupName: string,
    systemTopicName: string,
    nextLink: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicNextOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsListBySystemTopicNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, systemTopicName, nextLink, options },
      listBySystemTopicNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscription,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscription,
    },
    201: {
      bodyMapper: Mappers.EventSubscription,
    },
    202: {
      bodyMapper: Mappers.EventSubscription,
    },
    204: {
      bodyMapper: Mappers.EventSubscription,
    },
    default: {},
  },
  requestBody: Parameters.eventSubscriptionInfo,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}",
  httpMethod: "DELETE",
  responses: { 200: {}, 201: {}, 202: {}, 204: {}, default: {} },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscription,
    },
    201: {
      bodyMapper: Mappers.EventSubscription,
    },
    202: {
      bodyMapper: Mappers.EventSubscription,
    },
    204: {
      bodyMapper: Mappers.EventSubscription,
    },
    default: {},
  },
  requestBody: Parameters.eventSubscriptionUpdateParameters,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getFullUrlOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}/getFullUrl",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscriptionFullUrl,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listBySystemTopicOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscriptionsListResult,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getDeliveryAttributesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/systemTopics/{systemTopicName}/eventSubscriptions/{eventSubscriptionName}/getDeliveryAttributes",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.DeliveryAttributeListResult,
    },
    default: {},
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.eventSubscriptionName,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listBySystemTopicNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EventSubscriptionsListResult,
    },
    default: {},
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
    Parameters.systemTopicName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
